import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert, Spinner, Button } from 'react-bootstrap';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, PersonFill, Eye } from 'react-bootstrap-icons';
import { SimpleSection } from '../components/Section';
import config from '../config';

const ReviewPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthAndFetchReviews();
  }, []);

  const checkAuthAndFetchReviews = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Fetch assigned reviews for this user
      await fetchAssignedReviews(user.attributes.sub);
    } catch (error) {
      console.error('Authentication error:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedReviews = async (userId) => {
    try {
      const response = await fetch(`${config.api.url}reviewer/assignments/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch reviews: ${response.status}`);
      }

      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load your assigned reviews. Please try again later.');
    }
  };

  const formatDate = (timestamp) => {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    return new Date(utcTimestamp).toLocaleString(undefined, options);
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'in_progress':
        return <Badge bg="primary">In Progress</Badge>;
      case 'assigned':
      default:
        return <Badge bg="warning" text="dark">Pending</Badge>;
    }
  };

  const handleStartReview = (reviewId, submissionId) => {
    navigate(`/review/submission/${submissionId}`);
  };

  if (loading) {
    return (
      <Container className="my-4">
        <Row>
          <Col>
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Loading your assigned reviews...</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container className="my-4">
        <Row>
          <Col>
            <Alert variant="warning">
              <Alert.Heading>Authentication Required</Alert.Heading>
              <p>
                You need to be logged in to access your reviews. Please{' '}
                <Alert.Link onClick={() => navigate('/user')}>
                  sign in
                </Alert.Link>{' '}
                to continue.
              </p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (<>
    {/* Header Section */}
    <SimpleSection>
      <h1>
        My Review Assignments
      </h1>
      <p className="text-muted mb-0">
        Welcome {currentUser?.attributes?.name || currentUser?.attributes?.email}
      </p>
    </SimpleSection>

    {/* Reviews Section */}
    <SimpleSection>
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {reviews.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h4>No Review Assignments</h4>
            <p className="text-muted">
              You don't have any review assignments at the moment.<br/>
              Check back later or contact the administrators if you believe this is an error.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FileText className="me-2" />
                Assigned Reviews
              </h5>
              <Badge bg="info" className="fs-6">
                {reviews.length} Assignment{reviews.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Submission</th>
                  <th>Authors</th>
                  <th>Track</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.reviewId}>
                    <td>
                      <div>
                        <strong>{review.submission?.title || 'Untitled Submission'}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        {
                          review.submission.authors.map((author)=>{
                            return <div>
                              <PersonFill className="me-1" size={14} />
                              {author.name}
                              {author.affiliation && <span className="text-muted">({author.affiliation})</span>}
                            </div>
                          })
                        }
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">
                        {review.submission?.track || 'Unknown'}
                      </Badge>
                    </td>
                    <td>
                      <div className="small">
                        <Calendar className="me-1" size={14} />
                        {review.submission?.timestamp 
                          ? formatDate(review.submission.timestamp)
                          : 'Unknown'
                        }
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(review.status)}
                    </td>
                    <td>
                      <Button
                        variant={review.status === 'completed' ? 'outline-primary' : 'primary'}
                        size="sm"
                        onClick={() => handleStartReview(review.reviewId, review.submissionId)}
                      >
                        <Eye className="me-1" size={14} />
                        {review.status === 'completed' ? 'View' : (review.status==='in_progress' ? 'Continue':'Review')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </SimpleSection>

    {/* Stats Section */}
    {reviews.length > 0 && (
      <SimpleSection>
        <h5 className="mb-3">Review Statistics</h5>
        <Row>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-warning">
                  {reviews.filter(r => r.status === 'assigned').length}
                </h4>
                <small className="text-muted">Pending</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-primary">
                  {reviews.filter(r => r.status === 'in_progress').length}
                </h4>
                <small className="text-muted">In Progress</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-success">
                  {reviews.filter(r => r.status === 'completed').length}
                </h4>
                <small className="text-muted">Completed</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-info">
                  {reviews.length}
                </h4>
                <small className="text-muted">Total</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </SimpleSection>
    )}
  </>);
};

export default ReviewPage;
